import {
  Box,
  Chip,
  Divider,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import {
  PlayArrow,
  Stop,
  AccountTree,
  Timer,
} from "@mui/icons-material";
import React from "react";
import { getTranslation } from "common";
import { useTranslations } from "hooks";

// -------------------------
// 1. Tipi
// -------------------------
export interface HistoryEntry {
  task_name: string;
  owner: string;
  start_time: string;
  end_time: string;
  duration: string;
  state: string;
  subprocesses: HistoryEntry[] | HistoryEntry[][];
}

interface HistoriesSectionProps {
  history: HistoryEntry[];
}

// -------------------------
// 2. Utility functions
// -------------------------

// Flatten nested subprocesses array (API returns [[...]] instead of [...])
const flattenSubprocesses = (subprocesses: any[]): HistoryEntry[] => {
  if (!subprocesses || subprocesses.length === 0) return [];

  // Se il primo elemento è un array, appiattisci
  if (Array.isArray(subprocesses[0])) {
    return subprocesses.flat().map((sub: any) => ({
      ...sub,
      subprocesses: flattenSubprocesses(sub.subprocesses || []),
    }));
  }

  // Altrimenti, processa ricorsivamente
  return subprocesses.map((sub: any) => ({
    ...sub,
    subprocesses: flattenSubprocesses(sub.subprocesses || []),
  }));
};

const formatTime = (timeString: string) => {
  try {
    const date = new Date(timeString);
    return {
      full: date.toLocaleString("it-IT", {
        weekday: "short",
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      }),
      short: date.toLocaleString("it-IT", {
        day: "2-digit",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      }),
      time: date.toLocaleString("it-IT", {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      }),
      date: date.toLocaleString("it-IT", {
        weekday: "short",
        day: "2-digit",
        month: "long",
        year: "numeric",
      }),
    };
  } catch {
    return {
      full: timeString,
      short: timeString,
      time: timeString,
      date: timeString,
    };
  }
};

const formatDuration = (duration: string, translations: any) => {
  const parts = duration?.split(":");
  if (parts?.length === 3) {
    const hours = parseInt(parts[0]);
    const minutes = parseInt(parts[1]);
    const seconds = parseInt(parts[2]);

    let formatted = "";
    if (hours > 0) formatted += `${hours}h `;
    if (minutes > 0) formatted += `${minutes}m `;
    if (seconds > 0 || formatted === "") formatted += `${seconds}s`;

    return {
      short: formatted.trim(),
      long: `${hours} ${getTranslation(translations, "pm.propertyeditor.specificproperties.repetition_details.duration.label.hours")}, ${minutes} ${getTranslation(translations, "pm.propertyeditor.specificproperties.repetition_details.duration.label.minutes")}, ${seconds} ${getTranslation(translations, "generic.seconds.caption")}`,
    };
  }
  return { short: duration, long: duration };
};

// -------------------------
// 3. Componente Timeline Connector
// -------------------------
const TimelineConnector: React.FC<{ isLast: boolean; hasChildren: boolean }> = ({
  isLast,
  hasChildren,
}) => {
  if (isLast && !hasChildren) return null;

  return (
    <Box
      sx={{
        position: "absolute",
        left: 19,
        top: 44,
        bottom: hasChildren ? 0 : -24,
        width: 2,
        bgcolor: "f0f0f0",
        zIndex: 0,
        "&::after": {
          content: '""',
          position: "absolute",
          width: 2,
          height: "100%",
          background: "linear-gradient(180deg, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0) 100%)",
        },
      }}
    />
  );
};

// -------------------------
// 4. Componente InfoTable per i tempi
// -------------------------
interface InfoTableProps {
  startTime: string;
  endTime: string;
  duration: string;
  isSubprocess?: boolean;
}

const InfoTable: React.FC<InfoTableProps> = ({ startTime, endTime, duration, isSubprocess = false }) => {
  const theme = useTheme();
  const translations = useTranslations();
  const startTimeFormatted = formatTime(startTime);
  const endTimeFormatted = formatTime(endTime);
  const durationFormatted = formatDuration(duration, translations);

  return (
    <Table size="small" sx={{ '& .MuiTableCell-root': { border: 0, py: 0.5 } }}>
      <TableBody>
        <TableRow>
          <TableCell sx={{ width: '33%', px: 1 }}>
            <Stack spacing={0.5}>
              <Stack direction="row" spacing={1} alignItems="center">
                <PlayArrow sx={{ fontSize: 18, color: theme.palette.success.main }} />
                <Typography
                  variant="caption"
                  sx={{
                    color: "text.secondary",
                    fontSize: "0.7rem",
                    fontWeight: 500,
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                  }}
                >
                  {getTranslation(translations, "pm.processstart.start.label")}
                </Typography>
              </Stack>
              <Tooltip title={startTimeFormatted.full} placement="top" arrow>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {startTimeFormatted.short}
                </Typography>
              </Tooltip>
            </Stack>
          </TableCell>

          <TableCell sx={{ width: '33%', px: 1 }}>
            <Stack spacing={0.5}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Stop sx={{ fontSize: 18, color: theme.palette.error.main }} />
                <Typography
                  variant="caption"
                  sx={{
                    color: "text.secondary",
                    fontSize: "0.7rem",
                    fontWeight: 500,
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                  }}
                >
                  {getTranslation(translations, "generic.end.caption")}
                </Typography>
              </Stack>
              <Tooltip title={endTimeFormatted.full} placement="top" arrow>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {endTimeFormatted.short}
                </Typography>
              </Tooltip>
            </Stack>
          </TableCell>

          <TableCell sx={{ width: '33%', px: 1 }}>
            <Stack spacing={0.5}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Timer sx={{ fontSize: 18, color: theme.palette.warning.main }} />
                <Typography
                  variant="caption"
                  sx={{
                    color: "text.secondary",
                    fontSize: "0.7rem",
                    fontWeight: 500,
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                  }}
                >
                  {getTranslation(translations, "generic.task.totalduration")}
                </Typography>
              </Stack>
              <Tooltip title={durationFormatted.long} placement="top" arrow>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {durationFormatted.short}
                </Typography>
              </Tooltip>
            </Stack>
          </TableCell>
        </TableRow>
      </TableBody>
    </Table>
  );
};

// -------------------------
// 5. Componente ricorsivo Timeline Item
// -------------------------
interface HistoryItemProps {
  item: HistoryEntry;
  level?: number;
  isLast?: boolean;
  index?: number;
}

const HistoryItem: React.FC<HistoryItemProps> = ({
  item,
  level = 0,
  isLast = false,
  index = 0,
}) => {
  const translations = useTranslations();
  const theme = useTheme();
  const {
    task_name,
    owner,
    start_time,
    end_time,
    duration,
    state,
    subprocesses: rawSubprocesses,
  } = item;

  // Flatten subprocesses se necessario
  const subprocesses = flattenSubprocesses(rawSubprocesses || []);
  const isSubprocess = level > 0;

  return (
    <Box position="relative">
      <TimelineConnector
        isLast={isLast}
        hasChildren={subprocesses && subprocesses.length > 0}
      />

      {/* Main content */}
      <Stack direction="row" spacing={2} alignItems="flex-start">

        <Paper
          elevation={isSubprocess ? 0 : 2}
          sx={{
            flex: 1,
            overflow: "hidden",
            position: "relative",
            bgcolor: "f0f0f0",
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
            borderLeft: `4px solid ${isSubprocess ? theme.palette.secondary.main : theme.palette.primary.main}`,
            transition: "all 0.2s ease",
            "&:hover": {
              boxShadow: theme.shadows[4],
              transform: "translateX(2px)",
            },
            "&::before": !isSubprocess
              ? {
                content: '""',
                position: "absolute",
                top: 14,
                left: -8,
                width: 0,
                height: 0,
                borderStyle: "solid",
                borderWidth: "8px 8px 8px 0",
                borderColor: `transparent ${theme.palette.background.paper} transparent transparent`,
              }
              : undefined,
          }}
        >
          <Box
            sx={{
              p: 2,
              pb: 1.5,
              bgcolor: "f0f0f0",
            }}
          >
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="flex-start"
              spacing={2}
            >
              <Box flex={1}>
                <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
                  <Box display="flex" flex={1} alignItems="center" justifyContent="space-between">
                    <Typography
                      variant={isSubprocess ? "body1" : "h6"}
                      fontWeight="bold"
                      color="text.primary"
                    >
                      {task_name}
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                      {isSubprocess && (
                        <Chip
                          icon={<AccountTree sx={{ fontSize: 14 }} />}
                          label={getTranslation(translations, "generic.subprocess")}
                          size="small"
                          variant="outlined"
                          color="secondary"
                          sx={{ height: 20, fontSize: "0.7rem" }}
                        />
                      )}
                      <Typography
                        variant="subtitle1"
                        fontWeight="bold"
                        color="text.primary"
                      >
                        {owner}
                      </Typography>
                    </Stack>
                  </Box>
                </Stack>

                {/* Task number badge */}
                {!isSubprocess && (
                  <Typography variant="caption" color="text.secondary">
                    {getTranslation(translations, "generic.task")} #{index + 1} • ID:{task_name.substring(0, 8)}...
                  </Typography>
                )}
                {isSubprocess && (
                  <Typography variant="caption" color="text.secondary">
                    {getTranslation(translations, "generic.task")} #{index + 1}
                  </Typography> 
                  // HO UN DUBBIO
                )}
              </Box>
            </Stack>
          </Box>

          <Divider />

          <Box sx={{ p: 2 }}>
            <Stack spacing={2}>
              <InfoTable
                startTime={start_time}
                endTime={end_time}
                duration={duration}
                isSubprocess={isSubprocess}
              />

              {subprocesses && subprocesses.length > 0 && (
                <Box
                  sx={{
                    mt: 1,
                    p: 1,
                    bgcolor: theme.palette.action.hover,
                    borderRadius: 1,
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center">
                    <AccountTree sx={{ fontSize: 18, color: "text.secondary" }} />
                    <Typography variant="caption" color="text.secondary">
                      {getTranslation(translations, "generic.task.have")} {subprocesses.length} {getTranslation(translations, "generic.subprocesses")}
                    </Typography>
                  </Stack>
                </Box>
              )}
            </Stack>
          </Box>
        </Paper>
      </Stack>

      {/* Render subprocesses ricorsivamente */}
      {subprocesses && subprocesses.length > 0 && (
        <Box ml={5} mt={2}>
          <Stack spacing={3}>
            {subprocesses.map((sub, idx) => (
              <HistoryItem
                key={`${task_name}-sub-${idx}`}
                item={sub}
                level={level + 1}
                isLast={idx === subprocesses.length - 1}
                index={idx}
              />
            ))}
          </Stack>
        </Box>
      )}
    </Box>
  );
};

// Componente principale
const HistoriesSection: React.FC<HistoriesSectionProps> = ({ history }) => {
  const theme = useTheme();
  const translations = useTranslations();

  if (!history || history.length === 0) {
    return (
      <Box
        sx={{
          textAlign: "center",
          py: 4,
          color: "text.secondary",
        }}
      >
        <Typography variant="body1">{getTranslation(translations, "generic.nohave.history")}</Typography>
      </Box>
    );
  }

  const totalTasks = history.length;

  return (
    <Box
      sx={{
        position: "relative",
        bgcolor: "f0f0f0",
        borderRadius: 2,
        minHeight: 400,
      }}
    >
      <Box
        sx={{
          pb: 2,
          bgcolor: "f0f0f0",
          borderBottom: `1px solid ${theme.palette.divider}`,
          borderRadius: "8px 8px 0 0",
        }}
      >
        <Stack direction="row" spacing={3} sx={{ mt: 2 }}>
          <Chip
            label={`${totalTasks} Task totali`}
            size="small"
            variant="outlined"
            sx={{ borderColor: theme.palette.primary.main, backgroundColor: theme.palette.background.paper }}
          />
        </Stack>
      </Box>

      <Box sx={{ p: 3 }}>
        <Stack spacing={3}>
          {history.map((item, index) => (
            <HistoryItem
              key={`history-${index}`}
              item={item}
              isLast={index === history.length - 1}
              index={index}
            />
          ))}
        </Stack>
      </Box>
    </Box>
  );
};

export default HistoriesSection;